import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import { brand } from '../thema';

const flow = keyframes`
  to { stroke-dashoffset: -24; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
`;

type Node = { x: number; y: number; w: number; label: string; sub: string; accent?: boolean };

// 標準技術構成をアーキテクチャ図として表現（API の TS は TypeScript）（Web / iOS / Android → API → DB on AWS）
const clients: Node[] = [
    { x: 20, y: 40, w: 160, label: 'Web', sub: 'React / Next.js' },
    { x: 200, y: 40, w: 160, label: 'iOS', sub: 'Swift / SwiftUI' },
    { x: 380, y: 40, w: 160, label: 'Android', sub: 'Kotlin / Compose' },
];
const api: Node = { x: 170, y: 220, w: 220, label: 'API', sub: 'Java / Go / Python / Rust / TS', accent: true };
const db: Node = { x: 170, y: 370, w: 220, label: 'Database', sub: 'RDB / NoSQL' };
const ci: Node = { x: 20, y: 470, w: 160, label: 'CI', sub: 'Unit / E2E tests' };
const docs: Node = { x: 380, y: 470, w: 160, label: 'Docs', sub: 'Markdown / GitHub' };

const NODE_H = 64;

const NodeBox: React.FC<{ node: Node }> = ({ node }) => (
    <g>
        <rect
            x={node.x}
            y={node.y}
            width={node.w}
            height={NODE_H}
            fill={node.accent ? 'rgba(69, 182, 175, 0.08)' : 'rgba(255, 255, 255, 0.02)'}
            stroke={node.accent ? brand.accent : 'rgba(255, 255, 255, 0.28)'}
        />
        <text x={node.x + 14} y={node.y + 26} fill="#ffffff" fontFamily={brand.fontMono} fontSize="14" letterSpacing="0.06em">
            {node.label}
        </text>
        <text x={node.x + 14} y={node.y + 46} fill="rgba(255, 255, 255, 0.55)" fontFamily={brand.fontMono} fontSize="11">
            {node.sub}
        </text>
    </g>
);

const HeroVisual = () => {
    const apiTop = api.y;
    const apiCx = api.x + api.w / 2;
    const links = [
        ...clients.map((c) => `M ${c.x + c.w / 2} ${c.y + NODE_H} V ${c.y + NODE_H + 58} H ${apiCx} V ${apiTop}`),
        `M ${apiCx} ${api.y + NODE_H} V ${db.y}`,
        `M ${ci.x + ci.w / 2} ${ci.y} V ${api.y + NODE_H / 2} H ${api.x}`,
        `M ${docs.x + docs.w / 2} ${docs.y} V ${api.y + NODE_H / 2} H ${api.x + api.w}`,
    ];

    return (
        <Box
            aria-hidden
            sx={{
                width: '100%',
                maxWidth: 600,
                '& .flow': {
                    animation: `${flow} 1.2s linear infinite`,
                },
                '& .pulse': {
                    animation: `${pulse} 2.4s ease-in-out infinite`,
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '& .flow, & .pulse': { animation: 'none' },
                },
            }}
        >
            <svg viewBox="0 0 560 560" width="100%" role="presentation">
                {/* AWS 境界 */}
                <rect x="150" y="190" width="260" height="270" fill="none" stroke="rgba(255, 255, 255, 0.18)" strokeDasharray="4 6" />
                <text x="160" y="182" fill={brand.accent} fontFamily={brand.fontMono} fontSize="11" letterSpacing="0.12em">AWS</text>

                {/* 接続線（下地 + 流れる破線） */}
                {links.map((d) => (
                    <g key={d}>
                        <path d={d} fill="none" stroke="rgba(255, 255, 255, 0.14)" />
                        <path className="flow" d={d} fill="none" stroke={brand.accent} strokeWidth="1.25" strokeDasharray="4 20" />
                    </g>
                ))}

                {clients.map((c) => <NodeBox key={c.label} node={c} />)}
                <NodeBox node={api} />
                <NodeBox node={db} />
                <NodeBox node={ci} />
                <NodeBox node={docs} />

                {/* ステータス表示 */}
                <circle className="pulse" cx={api.x + api.w - 18} cy={api.y + 18} r="4" fill={brand.accent} />
                <text x={api.x + api.w - 30} y={api.y + 22} textAnchor="end" fill="rgba(255, 255, 255, 0.55)" fontFamily={brand.fontMono} fontSize="10">
                    200 OK
                </text>
            </svg>
        </Box>
    );
};

export default HeroVisual;
